import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography, IconButton, Box, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Card, CardContent, useMediaQuery, useTheme
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { categoryService } from '../services/categoryService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';

const Categories = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });
  const { user } = useAuth();

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await categoryService.getAllCategories();
      setCategories(data);
    } catch (error) {
      toast.error('Kategorien konnten nicht geladen werden');
    }
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditMode(true);
      setSelectedCategory(category);
      setFormData({ name: category.name, description: category.description || '' });
    } else {
      setEditMode(false);
      setSelectedCategory(null);
      setFormData({ name: '', description: '' });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async () => {
    try {
      if (editMode) {
        await categoryService.updateCategory(selectedCategory.id, formData);
        toast.success('Kategorie erfolgreich aktualisiert');
      } else {
        await categoryService.createCategory(formData);
        toast.success('Kategorie erfolgreich erstellt');
      }
      handleCloseDialog();
      loadCategories();
    } catch (error) {
      toast.error(editMode ? 'Kategorie konnte nicht aktualisiert werden' : 'Kategorie konnte nicht erstellt werden');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Kategorie wirklich löschen?')) {
      try {
        await categoryService.deleteCategory(id);
        toast.success('Kategorie erfolgreich gelöscht');
        loadCategories();
      } catch (error) {
        toast.error('Kategorie konnte nicht gelöscht werden');
      }
    }
  };

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700, background: 'linear-gradient(135deg, #CC0000 0%, #8B0000 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Kategorien</Typography>
        {user?.role === 'admin' && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Kategorie hinzufügen
          </Button>
        )}
      </Box>

      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {categories.map((category) => (
            <Card key={category.id}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{category.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{category.description || 'Keine Beschreibung'}</Typography>
                  </Box>
                  {user?.role === 'admin' && (
                    <Box display="flex">
                      <IconButton size="small" onClick={() => handleOpenDialog(category)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(category.id)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
          {categories.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}><Typography color="textSecondary">Keine Kategorien gefunden</Typography></Paper>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Beschreibung</TableCell>
                {user?.role === 'admin' && <TableCell>Aktionen</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>#{category.id}</TableCell>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>{category.description || '-'}</TableCell>
                  {user?.role === 'admin' && (
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenDialog(category)}><Edit /></IconButton>
                      <IconButton size="small" onClick={() => handleDelete(category.id)}><Delete /></IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {categories.length === 0 && (
            <Box p={3} textAlign="center"><Typography color="textSecondary">Keine Kategorien gefunden</Typography></Box>
          )}
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Kategorie bearbeiten' : 'Neue Kategorie hinzufügen'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Beschreibung"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editMode ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Categories;
